import { useState, type FormEvent } from 'react';
import { Check, Copy, AlertTriangle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '../../components/ui/dialog.tsx';
import { Button } from '../../components/ui/button.tsx';
import { Input } from '../../components/ui/input.tsx';
import { Label } from '../../components/ui/label.tsx';
import { useCreateProduct } from '../../lib/hooks.ts';
import { ApiError } from '../../lib/api.ts';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CreateProductDialog({ open, onOpenChange }: Props): JSX.Element {
  const [slug, setSlug] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [createdToken, setCreatedToken] = useState<string | null>(null);
  const createMutation = useCreateProduct();

  function reset(): void {
    setSlug('');
    setName('');
    setError(null);
    setCreatedToken(null);
  }

  function handleClose(next: boolean): void {
    if (!next) reset();
    onOpenChange(next);
  }

  async function onSubmit(e: FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);
    try {
      const result = await createMutation.mutateAsync({ slug: slug.trim(), name: name.trim() });
      setCreatedToken(result.api_token);
    } catch (err) {
      if (err instanceof ApiError && err.code === 'slug_conflict') {
        setError('Идентификатор уже занят. Выбери другой.');
      } else if (err instanceof ApiError && err.status === 422) {
        setError('Идентификатор должен быть из латинских букв, цифр и дефисов.');
      } else {
        setError(err instanceof Error ? err.message : 'Не удалось создать продукт');
      }
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent>
        {!createdToken ? (
          <form onSubmit={onSubmit}>
            <DialogTitle>Новый продукт</DialogTitle>
            <DialogDescription>
              Продукт получит уникальный API-токен. Токен показывается один раз — обязательно
              сохрани его в менеджер паролей.
            </DialogDescription>

            <div className="space-y-3">
              <div className="space-y-1.5">
                <Label htmlFor="product-name">Название</Label>
                <Input
                  id="product-name"
                  placeholder="Например: Self-Worth Channel"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  maxLength={200}
                  autoFocus
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="product-slug">Идентификатор</Label>
                <Input
                  id="product-slug"
                  placeholder="self-worth"
                  value={slug}
                  onChange={(e) => setSlug(e.target.value.toLowerCase())}
                  required
                  maxLength={100}
                  pattern="^[a-z0-9](?:[a-z0-9-]{0,98}[a-z0-9])?$"
                  className="font-mono"
                />
                <p className="text-xs text-fg-muted">
                  Только латиница, цифры и дефис. Заглавные приводятся к нижнему регистру
                  автоматически. Используется в логах и admin-tools.
                </p>
              </div>
            </div>

            {error ? (
              <p className="mt-3 rounded border border-danger/40 bg-danger/10 p-2 text-sm text-danger">
                {error}
              </p>
            ) : null}

            <div className="mt-6 flex justify-end gap-2">
              <Button type="button" variant="ghost" onClick={() => handleClose(false)}>
                Отмена
              </Button>
              <Button type="submit" variant="primary" disabled={createMutation.isPending}>
                {createMutation.isPending ? 'Создаём…' : 'Создать'}
              </Button>
            </div>
          </form>
        ) : (
          <CreatedTokenView token={createdToken} onClose={() => handleClose(false)} />
        )}
      </DialogContent>
    </Dialog>
  );
}

function CreatedTokenView({ token, onClose }: { token: string; onClose: () => void }): JSX.Element {
  const [copied, setCopied] = useState(false);

  async function copy(): Promise<void> {
    await navigator.clipboard.writeText(token);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }

  return (
    <div>
      <DialogTitle>Продукт создан</DialogTitle>
      <DialogDescription>Сохрани API-токен сейчас — повторно показан не будет.</DialogDescription>

      <div className="mb-3 flex items-start gap-2 rounded border border-danger/40 bg-danger/5 p-3 text-sm text-danger">
        <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
        <span>
          После закрытия окна токен исчезнет. Если потеряешь — нужно перевыпустить через настройки
          продукта.
        </span>
      </div>

      <div className="mb-2 flex items-center justify-between">
        <Label>API-токен</Label>
        <button
          onClick={() => void copy()}
          className="flex items-center gap-1 text-xs text-fg-muted hover:text-accent"
        >
          {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
          {copied ? 'Скопировано' : 'Скопировать'}
        </button>
      </div>
      <code className="block break-all rounded border border-border bg-bg p-3 font-mono text-xs">
        {token}
      </code>

      <div className="mt-6 flex justify-end">
        <Button variant="primary" onClick={onClose}>
          Готово
        </Button>
      </div>
    </div>
  );
}
